import { defineDocument } from '../../schema';
export const gameEgoManiacsDocument = defineDocument({
    id: 'game:ego-maniacs',
    kind: 'game',
    slug: 'ego-maniacs',
    route: '/discover/games/ego-maniacs',
    seo: {
        title: 'Discover Ego Maniacs',
        description: 'An elimination game where you vote on Ego Awards. Win one to stay safe or face elimination. Last player standing is the ultimate Ego Maniac.',
        imageAsset: 'coverEgoManiacs',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Ego Maniacs',
            },
        ],
        description: [
            {
                type: 'text',
                text: "An elimination game where players vote on awards for each other and themselves; fail to win one each round and you're out.",
            },
        ],
        imageAsset: 'coverEgoManiacs',
        imageAlt: 'Ego Maniacs game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Each round, a fresh batch of Ego Awards gets conjured up—but plot twist: there aren't enough trophies for everyone. Players vote on who deserves each award the most. Of course you can vote for yourself, you magnificent narcissist.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Snag an award and you're safe. Fail to impress? One unlucky soul gets the boot. Eliminated players still get to vote, so don't get too comfortable. The last player left standing is crowned the biggest Ego Maniac and gets to bask in the very accurate belief that they're better than everyone else.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '4-8 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Voting, Elimination',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~13 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'October 10, 2025',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '68016cb23f0914bff706b7ca',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'A new set of Ego Awards is generated for the round. There are one fewer awards than the number of uneliminated players.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players vote on who deserves each award most. You can (and are encouraged to) nominate yourself.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: "Players receiving the majority of votes for an award will earn it. If there is a tie, a judge will vote to break it. The judge is selected based on the following priority: impartial players (didn't nominate a tied player for this award) → partial players (did nominate a tied player for this award) → one of the tied players themselves.",
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players who receive an award are safe for the round. Safe players are not eligible to win any other awards in the round and are excluded from the remaining majority vote calculations.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The player without an award at the end of the round is eliminated from the game. Eliminated players continue to vote in subsequent rounds.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Play continues until only one player remains.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'One point per trophy/award earned per round.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The last player standing will have the most awards and win.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'None',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Ego Awards',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Trophy images for each award',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: low',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: people who know each other well, teams, in-person games',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/the-return-of-the-king-ego-maniacs-reimagined',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Ego Maniacs blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '68016cb23f0914bff706b7ca',
                },
            ],
        },
    ],
});
//# sourceMappingURL=ego-maniacs.js.map